import React, { FormEvent } from "react";
import styles from "./LoanData.scss";
import cx from "../../utils/cx";
// import Tooltip from "../Tooltip";
import NumberField from "../MaterialUi/NumberField";
import Translate from "../../utils/Translate";
import { getFinancingDataFromPostalCode } from "./postalCode2FinancingData";
// import Button from "../Button";
import { IFinancialCheckData } from "./FinancialCheckPage";
import Icon from "../MaterialUi/Icon";
import Button from "../MaterialUi/Button";
import Tooltip from "../Tooltip";
import { DEFAULT_PRICE, IBaufiImplecoLeadPageProps } from "./BaufiImplecoLeadPage";

export interface ILoanDataProps {
  className?: string;
  nextStep: () => void;
  setData: (data?: IFinancialCheckData) => void;
  data: IFinancialCheckData;
  prevStep?: () => void;
  variant?: IBaufiImplecoLeadPageProps['variant'];
}

const LoanData: React.FC<ILoanDataProps> = ({ data, nextStep, setData, className = '', variant }) => {

    const [isLoading, setIsLoading] = React.useState(false);

    const propertyData = data?.property; // default values if property is not set
    const financingData = propertyData?.field_address?.postal_code 
        ? getFinancingDataFromPostalCode(propertyData?.field_address?.postal_code) 
        : {
                stampDutyPercentage: 4, // Grunderwerbssteuer
                notarialCostsPercentage: 1.5, // Notarkosten
                findersFeePercentage: 3, // Maklercourtage
            };
    const commissionPercentage = !propertyData || propertyData?.commissionable ? Number(propertyData?.commission) || financingData.findersFeePercentage : 0;
    const defaultClosingCostsPercentage = commissionPercentage + financingData.notarialCostsPercentage + financingData.stampDutyPercentage;

    const defaultPrice = Number(propertyData?.price) || DEFAULT_PRICE;
    const defaultClosingCosts = Math.round(defaultPrice * defaultClosingCostsPercentage) / 100;
    const defaultEquity = Math.round(defaultPrice / 500) * 100; // Eigenkapital, 20%

    const [fields, setFields] = React.useState(data?.loanData || {
        price: defaultPrice,
        closingCosts: defaultClosingCosts,
        equity: defaultEquity,
        loan: defaultPrice + defaultClosingCosts - defaultEquity,
        remainingDebt: 0
    });

    const equityPercentage = fields.equity / fields.price * 100;
    const closingCostsPercentage = fields.closingCosts / fields.price * 100;

    const handleChange = (name: string, value: number) => {
        const nextFields = { ...fields, [name]: value };
        setFields({
            ...nextFields,
            loan: Math.max(0, nextFields.price + nextFields.closingCosts - nextFields.equity),
        });
        setData({ leadResponse: undefined })
    }

    const handleSubmit = (ev: FormEvent) => {
        ev.preventDefault();
        setData({ loanData: fields });
        nextStep();
        // const postalCode = homeData?.field_address?.postal_code;
        // if (postalCode) {

        //     if (data?.loanData && data?.campaign) {
        //         setData({ loanData: fields });
        //     } else {
        //         setIsLoading(true);
        //         LeadSmartApi.leadPreflight(postalCode).then((res) => {

        //             if (!res.leadConsumers[0]) {
        //                 createToast('Der Finanzierungsrechner ist momentan nicht verfügbar.', 'error');
        //                 return;
        //             }

        //             setData({ loanData: fields, campaign: res.leadConsumers[0] });
        //         }).catch((e) => {
        //             createToast('Der Finanzierungsrechner ist für diese Postleitzahl nicht verfügbar.', 'error');
        //             console.error(e);
        //         }).finally(() => {
        //             setIsLoading(false);
        //         });
        //     }
        //     nextStep();
        // } else {
        //     createToast('Der Finanzierungsrechner benötigt eine Postleitzahl der Immobilie.', 'error');
        // }
    }

    return <div className={cx(styles.loanData, className)}>

        { variant === 'propertyMessage' 
            ? <h3>Im nächsten Schritt können wir gerne noch deine Finanzierungschancen prüfen - mit sofortigem Finanzierungscheck</h3>
            : <h1>Lass uns deine Finanzierungschancen prüfen   – mit sofortigem Finanzierungs&shy;check</h1>
        }

        <p>
            Mit den folgenden Daten können wir die Machbarkeit einer Finanzierung für dich checken. 
            Damit schaffst du mehr Planungssicherheit zwischen dir, den Immobilien-Anbietern und deinem Finanzierungsberater.
        </p>


        <form onSubmit={handleSubmit}>
            <dl>
                <dt><span>&nbsp;</span>Kaufpreis</dt>
                <dd>
                    <NumberField name="price" value={fields.price} label="" onChange={handleChange} suffix=" €" />
                    <Tooltip>
                        Das ist der aktuell angebotene Kaufpreis der Immobilie.
                        Dieser Kaufpreis kann, abhängig von Bonität und finanzieller Situation,
                        zum Teil durch eine Immobilienfinanzierung durch eine Bank finanziert werden.
                    </Tooltip>
                </dd>

                <dt>
                    <span>+</span>
                    Kaufnebenkosten
                    <small>({Translate.getNumber(closingCostsPercentage)}%)</small></dt>
                <dd>
                    <NumberField name="closingCosts" value={fields.closingCosts} label="" onChange={handleChange} suffix=" €" />
                    <Tooltip>
                        Die Kaufnebenkosten werden aus allen vorliegenden Daten (Kaufpreis, Standort etc.) prozentual vom Kaufpreis berechnet und setzen sich folgendermaßen zusammen:<br />
                        <br />
                        Grunderwerbssteuer: {Translate.getNumber(Math.round(financingData.stampDutyPercentage * fields.price / 100))} ({financingData.stampDutyPercentage}%)<br />
                        Grundbucheintrag: {Translate.getNumber(Math.round(0.5 * fields.price / 100))}€ (0,50%)<br />
                        Notar: {Translate.getNumber(Math.round((financingData.notarialCostsPercentage - 0.5) * fields.price / 100))}€ ({financingData.notarialCostsPercentage - 0.5}%)<br />
                        Maklercourtage: {Translate.getNumber(Math.round(commissionPercentage * fields.price / 100))}€ ({commissionPercentage}%)<br />
                        <br />
                        Empfohlen wird, dass mindestens die Kaufnebenkosten durch das Eigenkapital gedeckt werden.
                    </Tooltip>
                </dd>

                <dt>
                    <span>-</span>
                    Eigenkapital
                    <small>({Translate.getNumber(equityPercentage)}%)</small>
                </dt>
                <dd>
                    <NumberField name="equity" value={fields.equity || ''} label="" onChange={handleChange} suffix={fields.equity ? ' €' : undefined} />
                    <Tooltip>
                        Das Eigenkapital ist persönliches Vermögen, welches für den Kauf der Immobilie eingesetzt werden soll.
                        Es reduziert zum einen die Darlehenshöhe, zum anderen dient es der Bank als Sicherheit und reduziert damit den Zinssatz.<br />
                        <br />
                        Zum Eigenkapital gehören direkt einsetzbare Vermögen wie Bargeld, Geld auf Giro-, Tagesgeld- und Festgeldkonten,
                        aber auch Wertpapier wie Fonds oder Aktien, sowie zuteilungsreife Bausparverträge.
                        Außerdem können lastenfreie Immobilien oder eine kapitalbildende Lebensversicherung als weitere Sicherheiten hinterlegt werden.
                    </Tooltip>
                </dd>

                <dt><span>=</span>Finanzierung</dt>
                <dd>
                    <NumberField name="loan" value={fields.loan} label="" onChange={handleChange} suffix=" €" />
                    <Tooltip>
                        Das ist der Betrag der voraussichtlich durch eine Bank finanziert werden muss.
                        Das ist nur eine erste Indikation und wird im Beratungsprozess noch einmal detaillierter betrachtet.<br />
                        <br />
                        Die Höhe des bewilligten Darlehens hängt insbesondere auch vom Wert der als Sicherheit dienenden Immobilie (Beleihungswert) ab.
                    </Tooltip>
                </dd>

                <dt><span></span>bestehende Verbindlichkeiten</dt>
                <dd>
                    <NumberField 
                        name="remainingDebt" 
                        value={fields.remainingDebt || ''} 
                        label="" onChange={handleChange} 
                        suffix={fields.remainingDebt ? ' €' : undefined} 
                    />
                    <Tooltip>
                        Die Restschuld aus anderen Darlehen ist relevant, um die maximale monatliche Kreditbelastung durch ein 
                        Immobiliardarlehen abzuschätzen.
                    </Tooltip>
                </dd>
            </dl>

            <div className={styles.actions}>
                <div></div>
                {/* {Responsive.isSmartphone() ?
                    <Button 
                    variant="outlined" 
                    color="inherit" 
                    href={`/property/${homeData?.uid}`} 
                    startIcon={<Icon name="navigatePrev" />} />
                    : <Button 
                    variant="outlined" 
                    color="inherit" 
                    href={`/property/${homeData?.uid}`} 
                    startIcon={<Icon name="navigatePrev" />}
                    >zum Exposé zurück</Button>} */}
                <Button 
                    color="primary" 
                    loading={isLoading} 
                    endIcon={<Icon name="navigateNext" />} 
                    type="submit"
                >Finanzierung checken</Button>
            </div>

            <p className={styles.terms}>Bitte beachte unsere <a href="/info/datenschutz" target="_blank">Datenschutzhinweise</a></p>

        </form>
    </div>
};

export default LoanData;
